import type {SharedBulkActionsItemPickerProps} from '@github-ui/item-picker-base/ItemPicker'
import type {LabelPickerBaseProps, LabelPickerLabel} from '@github-ui/item-picker-label'
import {
  BulkIssueLabelPicker as BulkIssueLabelPickerTsq,
  type BulkIssueLabelPickerProps as BulkIssueLabelPickerTsqProps,
} from '@github-ui/item-picker-label-tsq/BulkIssueLabelPicker'
import {useCallback} from 'react'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useRelayEnvironment} from 'react-relay'

import {addLabelsToStore, updateIssuesById} from './relay-tsq-interop'
import {usePreloadedLabels} from './use-preloaded-labels'

export type LabelPickerProps = Omit<LabelPickerBaseProps, 'filterItems'> & {
  labelNames?: string[]
}

type BulkItemPickerLabelProps = Omit<LabelPickerProps, 'onSelectionChange' | 'filterItems' | 'labels'> & {
  existingIssueLabels: Map<string, string[]>
  labelAppliedToAll: LabelPickerLabel[]
  connectionIds: {[key: string]: string[]}
} & SharedBulkActionsItemPickerProps

/**
 * Relay wrapper for bulk issue label picker with TSQ integration.
 *
 * This wrapper bridges Relay GraphQL data fetching and optimistic updates with the TSQ
 * (TanStack Query) bulk label picker. It provides:
 *
 * 1. Relay store optimistic updates via environment.commitUpdate
 * 2. Preloaded label data from Relay to TSQ component
 * 3. Integration with existing Relay mutation patterns
 *
 * The wrapper transforms label mutations into Relay store updates and ensures
 * the UI reflects changes immediately before server confirmation.
 */
export function BulkIssuesItemPickerLabelTsqWithRelayWrapper({
  connectionIds,
  existingIssueLabels,
  issuesToActOn,
  ...props
}: Omit<BulkItemPickerLabelProps, 'onSelectionChanged'>) {
  const environment = useRelayEnvironment()
  const onMutate: BulkIssueLabelPickerTsqProps['onMutate'] = useCallback(
    input => {
      environment.commitUpdate(store => {
        // Add labels to store if they're being added and labels data is provided
        if (input.addedLabelIds.length > 0 && input.labels) {
          // Filter labels to only those being added
          const labelsToAdd = input.labels.filter(label => input.addedLabelIds.includes(label.id))
          addLabelsToStore(store, labelsToAdd)
        }

        const inputArgs = {
          applyLabelIds: input.addedLabelIds,
          removeLabelIds: input.removedLabelIds,
        }
        updateIssuesById(store, issuesToActOn, existingIssueLabels, connectionIds, inputArgs)
      })
    },
    [connectionIds, environment, existingIssueLabels, issuesToActOn],
  )

  const preloadedLabels = usePreloadedLabels(props.owner, props.repo)

  return (
    <BulkIssueLabelPickerTsq
      {...props}
      issuesToActOn={issuesToActOn}
      onMutate={onMutate}
      preloadedLabels={preloadedLabels}
    />
  )
}

try{ BulkIssuesItemPickerLabelTsqWithRelayWrapper.displayName ||= 'BulkIssuesItemPickerLabelTsqWithRelayWrapper' } catch {}